import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import clsx from "clsx";
import {
  deleteStop,
  deleteStops,
  deleteUnscheduledSelectedStops
} from "../../../store/actions/custom";
import {
  reopenExtendable,
  setExtendableType
} from "../../../store/actions/edit";

import { Box, makeStyles } from "@material-ui/core";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { List } from "react-virtualized";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/AddCircleOutline";
import Fade from "@material-ui/core/Fade";
import Link from "@material-ui/core/Link";
import LoadingCircle from "../../common/LoadingCircle";
import UnscheduledInput from "./UnscheduledInput";
import DragItem from "../../optimized/DragItem";
import { setHoverStop } from "../../../store/actions/map";
import {
  setDragSelection,
  setDragSelectionByArr,
  setInfoDialog
} from "../../../store/actions/common";
import ConfirmDialog from "../../dialogs/ConfirmDialog";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import {
  checkIsFOJW,
  exportToXlsx,
  generateStopDataForXLSX,
  makeTextPlural
} from "../../../utils/helpers";
import { useFirebaseRemoteConfig } from "../../../states/firebase";

import "../../../customizedLibs/phoneInput/style/style.css";
import SearchBox from "../../common/SearchBox";
import { headerRow } from "../../data/constants";
import UnscheduledToolbar from "./UnscheduledToolbar";
import SearchBoxMessage from "../../common/SearchBoxMessage";
import { FormattedText } from "../../common/FormattedText";
import useListKeyboardCommands from "../../project/useListKeyboardCommands";

const useStyles = makeStyles(theme => ({
  header: {
    borderBottom: theme.variables.border,
    minHeight: 81,
    boxSizing: "border-box"
  },
  title: {
    fontSize: "1.25rem",
    lineHeight: "1.4375rem",
    marginBottom: theme.spacing(1),
    fontWeight: 500
  },
  subtitle: {
    fontSize: "0.875rem",
    lineHeight: "1rem",
    letterSpacing: "0.001em"
  },
  body: {
    maxHeight: "100%",
    overflow: "auto"
  },
  gridRow: {},
  addBlock: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    paddingLeft: theme.spacing(1.75),
    marginTop: theme.spacing(2),

    "& span": {
      color: "#B6B6B6",
      fontSize: "0.875rem"
    },

    "& svg": {
      color: "#B6B6B6"
    },

    "&:hover svg": {
      color: "#5F6368"
    }
  },
  placeholder: {
    border: "1px dashed #C4C4C4",
    padding: theme.spacing(4, 2.5),
    backgroundColor: "#f7f7f7",
    borderRadius: theme.spacing(0.5),
    marginTop: theme.spacing(-12.25)
  },
  placeholderText: {
    lineHeight: "1.25rem",
    fontSize: ".875rem"
  },
  noHeight: {
    height: "0px !important",
    minHeight: "0px !important"
  },
  noTopMargin: {
    marginTop: theme.spacing(0)
  },
  searchBoxMessage: {
    paddingTop: theme.spacing(3)
  }
}));

function Unscheduled({
  solution,
  loadingUnscheduled,
  reopenExtendable,
  setExtendableType,
  setHoverStop,
  projectStatus,
  deleteUnscheduledSelectedStops,
  canAdd,
  setInfoDialog,
  dragSelection,
  plan,
  setDragSelection,
  setDragSelectionByArr,
  isPublished
}) {
  const classes = useStyles();
  // const [inputActive, setInputActive] = useState();
  const [inputValue, setInputValue] = useState("");
  const [haveHovered, setHaveHovered] = useState(false);
  const listKeyboardCommands = useListKeyboardCommands();

  // FOJW: surface the Odoo RouteTag (driver tag) on each unscheduled stop.
  // Default-on kill-switch — disable remotely by setting the flag to "false".
  const firebaseRemoteConfig = useFirebaseRemoteConfig();
  const showRouteTag =
    checkIsFOJW() &&
    firebaseRemoteConfig.getString("ft_routetag_in_planner") !== "false";

  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    index: null,
    stop: null
  });

  const unassigned = solution ? solution.unassigned.stops : [];
  const dragSelectionList = dragSelection?.list ? dragSelection.list : [];

  const handleFilter = (stop, value) => {
    return (
      (stop.formatted_address || "").toLowerCase().indexOf(value) !== -1 ||
      (stop.information.recipient_name || "").toLowerCase().indexOf(value) !==
        -1 ||
      (stop?.tags?.map(tag => tag.name).join("") || "")
        .toLowerCase()
        .indexOf(value) !== -1
    );
  };

  const handleChange = value => {
    setInputValue(value);
    value = value.toLowerCase();
    const result = unassigned.filter(stop => handleFilter(stop, value));
    setFilteredData(result);
  };

  const handleSelectAllClick = event => {
    if (event.target.checked) {
      const newSelected = filteredData.map(n => n.id);
      setDragSelectionByArr("unassigned", newSelected);
      return;
    }
    setDragSelectionByArr("", []);
  };

  const [filteredData, setFilteredData] = useState(unassigned);
  const stopsForPrint = dragSelectionList.reduce((array, rowId) => {
    const row = unassigned.find(stop => stop.id === rowId);
    return [
      ...array,
      ...(!row.units || row.units.length === 0
        ? [{ ...row, units: [] }]
        : row.units.map((unit, i) => ({
            ...row,
            units: [unit]
          })))
    ];
  }, []);

  useEffect(() => {
    if (inputValue) {
      const value = inputValue.toLowerCase();
      const result = unassigned.filter(stop => handleFilter(stop, value));
      setFilteredData(result);
    } else {
      setFilteredData(unassigned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unassigned]);

  /*
  const handleAdd = () => {
    if (canAdd) setInputActive(true);
    else
      setInfoDialog({
        visible: true,
        title: "Operation not possible",
        body: `You’ve reached the maximum number of stops that you can add to one project. If you want to add more than ${plan.cap_stops_per_project} stops per project you need to upgrade your plan.`,
        button: {
          text: "Upgrade plan",
          link: "/payment/select-plan"
        },
        mainButtonText: "No, thanks"
      });
  };
  */
  // Close edit panel
  const handleClose = () => {
    setExtendableType(null);
  };

  const getItemStyle = (isDragging, draggableStyle, index) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",

    // zIndex: unassigned.length - index,

    // styles we need to apply on draggables
    ...draggableStyle
  });

  // Open edit panel for stop
  const editStop = useCallback(
    (e, id) => {
      e.stopPropagation();
      reopenExtendable("stops", id, 300, "unscheduled");
      setDragSelectionByArr("", []);
    },
    [reopenExtendable]
  );

  const handleItemClick = useCallback(
    (listId, itemId) => e => {
      setDragSelection(listId, itemId, e.ctrlKey || e.metaKey, e.shiftKey);
    },
    [setDragSelection]
  );

  const handleMultipleDownload = () => {
    const data = [
      headerRow,
      ...unassigned
        .filter(stop => dragSelection?.list.includes(stop.id))
        .reduce((array, row) => {
          return [
            ...array,
            ...(row.units && row.units.length < 1
              ? [generateStopDataForXLSX(row, 0)]
              : row.units.map((unit, i) => generateStopDataForXLSX(row, i))),
            
          ];
        }, [])
    ];

    exportToXlsx("unassigned.xlsx", data);
  };

  const handleMouseEnter = useCallback(
    stop => {
      setHoverStop({ stop });
    },
    [setHoverStop]
  );

  const handleMouseLeave = useCallback(
    () => setHoverStop(null),
    [setHoverStop]
  );

  useEffect(() => {
    if (unassigned.length === 0) {
      handleClose();
    }
  }, [unassigned.length]);

  useEffect(() => {
    listKeyboardCommands.methods.reset();
    listKeyboardCommands.methods.setListSize(filteredData.length);
  }, [filteredData, listKeyboardCommands.methods]);

  useEffect(() => {
    if (inputValue === "") {
      listKeyboardCommands.methods.stop();
    } else {
      listKeyboardCommands.methods.start();
    }
  }, [listKeyboardCommands.methods, inputValue]);

  listKeyboardCommands.methods.setEnterHandler((event, index) => {
    editStop(event, filteredData[index].id);
  });

  function rowRenderer({
    key, // Unique key within array of rows
    index, // Index of row within collection
    style // Style object to be applied to row (to position it)
  }) {
    const item = filteredData[index];

    if (!item) {
      return null;
    }
    return (
      <Draggable
        key={item.id}
        draggableId={item.id}
        index={index}
        isDragDisabled={isPublished}
      >
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
              ...style,
              ...getItemStyle(
                snapshot.isDragging,
                provided.draggableProps.style,
                index
              )
            }}
          >
            <DragItem
              item={item}
              index={index}
              editStop={editStop}
              projectStatus={projectStatus}
              deleteStop={setConfirmDialogState}
              container
              alignItems="center"
              className={clsx(classes.gridRow, "drag-item", [
                { "is-dragging": snapshot.isDragging }
              ])}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              unassigned
              hasSelected={dragSelection?.list?.length > 0}
              handleSelect={handleItemClick("unassigned", item.id)}
              editOnClick
              searchValue={inputValue}
              activeByListCommand={index === listKeyboardCommands.currentIndex}
              setHaveHovered={setHaveHovered}
              haveHovered={haveHovered}
              showRouteTag={showRouteTag}
            />
          </div>
        )}
      </Draggable>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      data-cy="unscheduled-extendable"
    >
      {/* Loading */}
      <Fade in={loadingUnscheduled} unmountOnExit mountOnEnter>
        <LoadingCircle />
      </Fade>

      <Box
        className={classes.header}
        display="flex"
        alignItems="start"
        px={3}
        py={1.7}
      >
        <Box mr={3} ml={-1} mt={0.5}>
          <OptimizedTooltip placement="bottom" title="Close">
            <IconButton onClick={handleClose} data-cy="unscheduledPanel-close">
              <CloseIcon />
            </IconButton>
          </OptimizedTooltip>
        </Box>
        <Box>
          <Typography className={classes.title} variant="h5">
            Unscheduled stops ({unassigned.length})
          </Typography>
          <Typography className={classes.subtitle} color="secondary">
            Move stops to a route by selecting them.
          </Typography>
        </Box>
      </Box>

      <Box>
        <UnscheduledToolbar
          stops={unassigned}
          selectedStopsList={dragSelection}
          searchInputValue={inputValue}
          setSearchInputValue={handleChange}
          handleSelectAllClick={handleSelectAllClick}
          handleMultipleDownload={handleMultipleDownload}
          stopsForPrint={stopsForPrint}
          deleteStop={setConfirmDialogState}
          editStop={editStop}
          onSearchFocus={() => {
            listKeyboardCommands.methods.enable();
          }}
          onSearchBlur={() => {
            listKeyboardCommands.methods.disable();
          }}
        />
        {/*
        <SearchBox
          unscheduled
          label="stop"
          data={unassigned}
          filteredData={filteredData}
          value={inputValue}
          selected={dragSelection?.list}
          handleChange={handleChange}
          handleSelectAllClick={handleSelectAllClick}
          handleMultipleDownload={handleMultipleDownload}
        />*/}
      </Box>

      <Box
        className={classes.body}
        display="flex"
        flexDirection="column"
        flex="1 1 100%"
      >
        {unassigned.length > 0 && filteredData.length === 0 && (
          <SearchBoxMessage
            message={`No stops found for "${inputValue}"`}
            className={classes.searchBoxMessage}
          />
        )}
        <Box>
          <Droppable
            droppableId="unassigned"
            mode="virtual"
            renderClone={(provided, snapshot, rubric) => (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={{
                  ...getItemStyle(
                    snapshot.isDragging,
                    provided.draggableProps.style,
                    rubric.source.index
                  )
                }}
              >
                <DragItem
                  item={filteredData[rubric.source.index]}
                  index={rubric.source.index}
                  editStop={editStop}
                  deleteStop={setConfirmDialogState}
                  container
                  projectStatus={projectStatus}
                  alignItems="center"
                  isDragging={snapshot.isDragging}
                  className={clsx(classes.gridRow, "drag-item", [
                    { "is-dragging": snapshot.isDragging }
                  ])}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  unassigned
                  showRouteTag={showRouteTag}
                />
              </div>
            )}
          >
            {(provided, snapshot) => {
              const itemCount =
                snapshot.isUsingPlaceholder && !snapshot.draggingFromThisWith
                  ? filteredData.length + 1
                  : filteredData.length;
              return (
                <List
                  height={Math.max(
                    Math.min(
                      // itemCount * 55,
                      window.innerHeight -
                        155 -
                        (dragSelection?.list?.length > 0 ? 60 : 0)
                    ),
                    !filteredData.length ? 98 : showRouteTag ? 72 : 55
                  )}
                  rowCount={itemCount}
                  rowHeight={showRouteTag ? 72 : 55}
                  rowRenderer={rowRenderer}
                  width={448}
                  style={{ width: "100%", minHeight: 30 }}
                  className={clsx({
                    [classes.noHeight]:
                      !filteredData.length && unassigned.length
                  })}
                  ref={ref => {
                    // react-virtualized has no way to get the list's ref that I can so
                    // So we use the `ReactDOM.findDOMNode(ref)` escape hatch to get the ref
                    if (ref) {
                      // eslint-disable-next-line react/no-find-dom-node
                      const whatHasMyLifeComeTo = ReactDOM.findDOMNode(ref);
                      if (whatHasMyLifeComeTo instanceof HTMLElement) {
                        provided.innerRef(whatHasMyLifeComeTo);
                      }
                    }
                  }}
                  scrollToIndex={listKeyboardCommands.currentIndex}
                />
              );
            }}
          </Droppable>
          {!unassigned.length && (
            <Box className={classes.placeholder}>
              <Typography className={classes.placeholderText} color="secondary">
                Unschedule a stop by dragging & dropping it into this area or in
                between unscheduled stops
              </Typography>
            </Box>
          )}

          {/* Add new stop 
          {inputActive ? (
            <Box position="relative" mt={2}>
              <UnscheduledInput hideInput={() => setInputActive(false)} />
            </Box>
          ) : (
            <Link
              component="button"
              type="button"
              underline="none"
              className={clsx(classes.addBlock, {
                [classes.noTopMargin]: !filteredData.length && unassigned.length
              })}
              onClick={handleAdd}
              data-cy="unscheduledPanel-addStop"
            >
              <OptimizedTooltip placement="bottom" title="Add stop">
                <AddIcon className={classes.texSecondary} />
              </OptimizedTooltip>

              <Box component="span" ml={2.25}>
                Add new stop
              </Box>
            </Link>
          )}*/}
        </Box>
      </Box>

      <ConfirmDialog
        isProject
        open={confirmDialogState.open}
        handleClose={() => setConfirmDialogState({ open: false })}
        action={() => deleteUnscheduledSelectedStops(dragSelectionList)}
        title={`Delete ${
          dragSelectionList.length === 1 ? "a" : ""
        } stop${makeTextPlural(dragSelectionList.length)}?`}
        body={`Are you sure you want to delete ${
          dragSelectionList.length > 1 ? dragSelectionList.length : "a"
        } stop${makeTextPlural(dragSelectionList.length)}?`}
      />
    </Box>
  );
}

function mapStateToProps(state) {
  const solution = state.project.solution;
  // Activities count without "start"s and "end"s
  const servicesQuantity =
    solution &&
    solution.routes.reduce((sum, route) => sum + route.stops.length - 2, 0) +
      solution.unassigned.stops.length;

  const plan =
    (state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation.plan) ||
    {};

  return {
    projectStatus: state.project.status,
    canAdd: plan.cap_stops_per_project > servicesQuantity,
    solution: state.project.solution,
    dragSelection:
      state.common.dragSelection.listId === "unassigned"
        ? state.common.dragSelection
        : null,
    loadingUnscheduled: state.loading.unscheduled,
    userType: state.auth.userData.userType,
    plan: plan,
    isPublished: ["published", "expired", "finished"].includes(
      state.project.status.toLowerCase()
    )
  };
}

export default connect(mapStateToProps, {
  reopenExtendable,
  setExtendableType,
  setInfoDialog,
  deleteUnscheduledSelectedStops,
  setHoverStop,
  setDragSelection,
  setDragSelectionByArr
})(Unscheduled);
